import { z } from 'zod';

import { ConnectorsDetailsMode } from '../../scenes/Connector/utils';

/**
 * Be aware that LaunchDarkly feature flags will be lowerCamelCased, regardless of how they are named.
 * For example: `FlowBuilderWorkTitle` will be `flowBuilderWorkTitle` and `flag-name` will be `flagName`.
 */
export enum FeatureFlagKey {
  testCanary = 'testCanary',
  markdownEditorOptions = 'markdownEditorOptions',
  flowBuilderRetiredBlockOptions = 'flowBuilderRetiredBlockOptions',
  flowBuilderSkillOptions = 'flowBuilderSkillOptions',
  flowDefinitionMetadataOptions = 'flowDefinitionMetadataOptions',
  blockConfigErrorsInDrawer = 'blockConfigErrorsInDrawer',
  flowBuilderBlockDescriptionOptions = 'flowBuilderBlockDescriptionOptions',
  flowCostEstimate = 'flowCostEstimate',
  canCreateCustomAnalyticEvents = 'canCreateCustomAnalyticEvents',
  enableV2Payments = 'enableV2Payments',
  canConfigureSignalV2 = 'canConfigureSignalV2',
  canConfigurePreAssistantMessage = 'canConfigurePreAssistantMessage',
  flowRunSafetyReview = 'flowRunSafetyReview',
  canConfigureAutoRedirectFromSchedulingPrimitive = 'canConfigureAutoRedirectFromSchedulingPrimitive',
  autoCompleteX12 = 'autoCompleteX12',
  testRunProductionPatientOptions = 'testRunProductionPatientOptions',
  deprecateAgentPages = 'deprecateAgentPages',
  enableSkipExportFormFieldButton = 'enableSkipExportFormFieldButton',
  enablePntDataSubmission = 'enablePntDataSubmission',
  appointmentVoiceReminders = 'appointmentVoiceReminders',
  enableSidekickAutomation = 'enableSidekickAutomation',
  ehrInstancesV2 = 'ehrInstancesV2',
  omniContentEmbedMode = 'omniContentEmbedMode',
  connectorsOptions = 'connectorOptions',
  voiceCallToolOptions = 'voiceCallToolOptions',
  flowInputAutoTypeOptions = 'flowInputAutoTypeOptions',
  inboundVoiceAssistant = 'inboundVoiceAssistant',
  triggerOptions = 'triggerOptions',
  feedValidationSettings = 'feedValidationSettings',
  advancedFhirBlockV2 = 'advancedFhirBlockV2',
  documentTemplatePreview = 'documentTemplatePreview',
  sidekickAiAutofill = 'sidekickAiAutofill',
  documentPacketGeneratorTool = 'documentPacketGeneratorTool',
  getNotableObjectAssistant = 'getNotableObjectAssistant',
  enableSidekickClinicalPacketTab = 'enableSidekickClinicalPacketTab',
}

export const FeatureFlagSchemas = {
  [FeatureFlagKey.testCanary]: z.string().default('None'),
  [FeatureFlagKey.markdownEditorOptions]: z
    .object({
      isEnabled: z.literal(true),
      flowDetails: z.object({
        isDocumentationEditable: z.boolean(),
      }),
      editDocumentationDialog: z.object({
        editorVariant: z.enum(['TextField', 'MarkdownEditor']),
      }),
    })
    .or(
      z.object({
        isEnabled: z.literal(false),
      })
    )
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.flowBuilderRetiredBlockOptions]: z
    .object({
      isEnabled: z.literal(true),
      alertVariant: z.enum(['warning', 'error']),
      card: z.object({
        tagContent: z.string().min(1),
      }),
      drawer: z.object({
        alertLine1: z.string().min(1),
        alertLine2PrefixNew: z.object({
          editable: z.string().min(1),
          nonEditable: z.string().min(1),
        }),
        alertLine2CallToActionSuffix: z.string().min(1),
        alertLine2Suffix: z.string().min(1),
      }),
      canvasAlertOverlay: z.object({
        isEnabled: z.boolean(),
      }),
    })
    .or(
      z.object({
        isEnabled: z.literal(false),
      })
    )
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.flowBuilderSkillOptions]: z
    .object({
      variant: z.literal('newPicker'),
      newPicker: z.object({
        favoritesPickingIsEnabled: z.boolean(),
        skillPickingIsEnabled: z.boolean(),
        subflowSkillPickingIsEnabled: z.boolean(),
        taggedSubflowPickingIsEnabled: z.boolean(),
      }),
    })
    .default({
      variant: 'newPicker',
      newPicker: {
        favoritesPickingIsEnabled: true,
        skillPickingIsEnabled: false,
        subflowSkillPickingIsEnabled: false,
        taggedSubflowPickingIsEnabled: false,
      },
    }),
  [FeatureFlagKey.flowDefinitionMetadataOptions]: z
    .object({
      isEnabled: z.literal(true),
    })
    .or(
      z.object({
        isEnabled: z.literal(false),
      })
    )
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.blockConfigErrorsInDrawer]: z.boolean().default(false),
  [FeatureFlagKey.flowBuilderBlockDescriptionOptions]: z
    .object({
      isEnabled: z.literal(true),
      blockCard: z.object({
        isDefaultValueFallbackUsed: z.boolean(),
        minHeightInPixels: z.number().min(0).max(400).default(80),
      }),
      descriptionField: z.object({
        isDefaultValuePrefilled: z.boolean(),
      }),
    })
    .or(
      z.object({
        isEnabled: z.literal(false),
      })
    )
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.flowCostEstimate]: z.boolean().default(false),
  [FeatureFlagKey.canCreateCustomAnalyticEvents]: z.boolean().default(false),
  [FeatureFlagKey.enableV2Payments]: z.boolean().default(false),
  [FeatureFlagKey.canConfigureSignalV2]: z.boolean().default(false),
  [FeatureFlagKey.canConfigurePreAssistantMessage]: z.boolean().default(false),
  [FeatureFlagKey.flowRunSafetyReview]: z
    .object({ isFlowRunSafetyReviewEnabled: z.boolean().default(false) })
    .default({ isFlowRunSafetyReviewEnabled: false }),
  [FeatureFlagKey.canConfigureAutoRedirectFromSchedulingPrimitive]: z.boolean().default(false),
  [FeatureFlagKey.autoCompleteX12]: z
    .object({
      isEnabled: z.boolean().default(false),
    })
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.testRunProductionPatientOptions]: z
    .object({
      canTestWithProductionPatient: z.boolean().default(false),
    })
    .default({
      canTestWithProductionPatient: false,
    }),
  [FeatureFlagKey.enableSkipExportFormFieldButton]: z
    .object({
      isEnableSkipExportFormFieldButton: z.boolean().default(false),
    })
    .default({
      isEnableSkipExportFormFieldButton: false,
    }),
  [FeatureFlagKey.enablePntDataSubmission]: z
    .object({
      enablePntDataSubmission: z.boolean(),
    })
    .default({
      enablePntDataSubmission: false,
    }),
  [FeatureFlagKey.deprecateAgentPages]: z
    .object({
      flowsPageMode: z.enum(['LegacyAgentsPage', 'NewFlowsPage']).default('LegacyAgentsPage'),
    })
    .default({
      flowsPageMode: 'LegacyAgentsPage',
    }),
  [FeatureFlagKey.appointmentVoiceReminders]: z
    .object({
      isEnabled: z.boolean(),
    })
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.enableSidekickAutomation]: z.boolean().default(false),
  [FeatureFlagKey.ehrInstancesV2]: z
    .object({
      isEnabled: z.boolean(),
    })
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.omniContentEmbedMode]: z
    .object({
      isEnabled: z.boolean(),
    })
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.connectorsOptions]: z
    .object({
      isListScreenEnabled: z.boolean(),
      detailsMode: z.nativeEnum(ConnectorsDetailsMode),
    })
    .default({
      isListScreenEnabled: false,
      detailsMode: ConnectorsDetailsMode.Drawer,
    }),
  [FeatureFlagKey.voiceCallToolOptions]: z
    .object({
      getAppointments: z.boolean(),
      getPatient: z.boolean(),
      getEncounters: z.boolean(),
      getOrders: z.boolean(),
    })
    .default({
      getAppointments: false,
      getPatient: false,
      getEncounters: false,
      getOrders: false,
    }),
  [FeatureFlagKey.flowInputAutoTypeOptions]: z
    .object({
      isEnabled: z.literal(true),
    })
    .or(
      z.object({
        isEnabled: z.literal(false),
      })
    )
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.inboundVoiceAssistant]: z
    .object({
      isViewAssistantsLandingPageEnabled: z.boolean(),
    })
    .default({
      isViewAssistantsLandingPageEnabled: false,
    }),
  [FeatureFlagKey.triggerOptions]: z
    .object({
      isEnabled: z.boolean(),
    })
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.feedValidationSettings]: z.boolean().default(false),
  [FeatureFlagKey.advancedFhirBlockV2]: z
    .object({
      isSearchEndpointEnabled: z.boolean(),
    })
    .default({
      isSearchEndpointEnabled: false,
    }),
  [FeatureFlagKey.documentTemplatePreview]: z
    .object({
      useV2: z.boolean(),
    })
    .default({
      useV2: false,
    }),
  [FeatureFlagKey.sidekickAiAutofill]: z
    .object({
      isEnabled: z.boolean(),
    })
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.documentPacketGeneratorTool]: z
    .object({
      isSubmitPriorAuthorizationEnabled: z.boolean(),
      isCustomSubtaskEnabled: z.boolean(),
    })
    .default({
      isSubmitPriorAuthorizationEnabled: false,
      isCustomSubtaskEnabled: false,
    }),
  [FeatureFlagKey.getNotableObjectAssistant]: z
    .object({
      isEnabled: z.boolean(),
    })
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.enableSidekickClinicalPacketTab]: z
    .object({
      isEnabled: z.literal(true),
    })
    .or(
      z.object({
        isEnabled: z.literal(false),
      })
    )
    .default({
      isEnabled: false,
    }),
} as const;
